import { useQueryClient } from "@tanstack/react-query";

import { ServerError } from "src/graphql-next/fetcher";
import { useGetMeQuery as useGetMeQueryOnNewGateway } from "src/graphql-next/generated/queries";
import { GetMeQuery as GetMeQueryOnNewGateway } from "src/graphql-next/generated/types";
import { useGetMeQuery } from "src/graphql/generated/queries";
import { FeatureFlagNamesEnum, GetMeQuery } from "src/graphql/generated/types";
import { getCustomerDisplayNameFromGatewayNext } from "src/utils/customer/getCustomerDisplayName";

import { useHasFeatureFlagActivated } from "./useHasFeatureFlagActivated";

export type Me = {
  login: string;
  isTemporary: boolean;
  fullname: string | undefined;
};

export type UseGetMeResult = {
  data: { me: Me } | undefined;
  isLoading: boolean;
  error: ServerError | null;
};

export type UseGetMeQueryDataResult = {
  data: { me: Me } | undefined;
};

export const useGetMe = (): UseGetMeResult => {
  const isGetMeOnNewGateway = useHasFeatureFlagActivated(
    FeatureFlagNamesEnum.CustomerPortalGetMeOnNewGateway
  );

  const {
    data: dataOnNewGateway,
    isLoading: isLoadingOnNewGateway,
    error: errorOnNewGateway,
  } = useGetMeQueryOnNewGateway(undefined, { enabled: isGetMeOnNewGateway });

  const { data, isLoading, error } = useGetMeQuery(undefined, { enabled: !isGetMeOnNewGateway });

  return isGetMeOnNewGateway
    ? {
        data: dataOnNewGateway && { me: mapMeOnNewGateway(dataOnNewGateway.me) },
        isLoading: isLoadingOnNewGateway,
        error: errorOnNewGateway,
      }
    : {
        data: data && { me: mapMe(data.me) },
        isLoading,
        error,
      };
};

export const useGetMeQueryData = (): UseGetMeQueryDataResult => {
  const queryClient = useQueryClient();

  const isGetMeOnNewGateway = useHasFeatureFlagActivated(
    FeatureFlagNamesEnum.CustomerPortalGetMeOnNewGateway
  );

  const queryDataOnNewGateway = queryClient.getQueryData<GetMeQueryOnNewGateway>(
    useGetMeQueryOnNewGateway.getKey()
  );

  const queryData = queryClient.getQueryData<GetMeQuery>(useGetMeQuery.getKey());

  return isGetMeOnNewGateway
    ? {
        data: queryDataOnNewGateway && { me: mapMeOnNewGateway(queryDataOnNewGateway.me) },
      }
    : {
        data: queryData && { me: mapMe(queryData.me) },
      };
};

function mapMe(me: GetMeQuery["me"]): Me {
  return {
    login: me.login,
    isTemporary: me.isTemporary,
    fullname: me.fullname ?? undefined,
  };
}

function mapMeOnNewGateway(me: GetMeQueryOnNewGateway["me"]): Me {
  return {
    login: me.login,
    isTemporary: me.isTemporaryLogin,
    fullname: getCustomerDisplayNameFromGatewayNext(me.customer) ?? undefined,
  };
}
